import { useState, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { config, type RestaurantConfig, type OperatingHour } from '../lib/api.js'
import {
  openModeOf,
  openModePayload,
  hoursPayload,
  hoursErrorMessage,
  type OpenMode,
} from '../lib/operations.js'
import { useI18n } from '../lib/i18n.js'
import {
  Card,
  Button,
  Input,
  Textarea,
  Switch,
  Select,
  toast,
  Spinner,
} from '../components/ui/index.js'
import { MenuThemePreview } from '../components/MenuThemePreview.js'
import { TemplateForm } from '../components/MenuTemplateForm.js'
import { TimezoneForm } from '../components/TimezoneForm.js'
import { cn } from '../lib/utils.js'

/**
 * #336: a parte de tradução (idiomas, execuções, refresh) saiu do Branding —
 * links/atalhos antigos que apontavam para a secção de traduções dentro de
 * /branding (âncora/hash ou ?section=/?tab=) caem em /r/:slug/translations.
 */
function LegacyTranslationsRedirect() {
  const { pathname, search, hash } = useLocation()
  const legacy = /translat/i.test(hash) || /[?&](?:section|tab)=[^&]*translat/i.test(search)
  if (!legacy) return null
  return <Navigate to={pathname.replace(/\/branding$/, '/translations')} replace />
}

export function Branding() {
  const qc = useQueryClient()
  const { t } = useI18n()

  const { data: cfg, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: () => config.get(),
  })

  const { data: hours = [], isLoading: loadingHours } = useQuery({
    queryKey: ['hours'],
    queryFn: () => config.getHours(),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <LegacyTranslationsRedirect />
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.branding')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('admin.branding.subtitle')}</p>
      </div>

      {cfg && <GeneralForm cfg={cfg} qc={qc} />}
      {cfg && <TemplateForm cfg={cfg} qc={qc} />}
      {cfg && <ColorsForm cfg={cfg} qc={qc} />}
      {cfg && <OperationsForm cfg={cfg} qc={qc} />}
      {cfg && <PaymentsForm cfg={cfg} qc={qc} />}
      {cfg && <WaiterLimitForm cfg={cfg} qc={qc} />}
      {cfg && <TimezoneForm cfg={cfg} qc={qc} />}
      {!loadingHours && <HoursForm hours={hours} qc={qc} />}
    </div>
  )
}

// ─── General ──────────────────────────────────────────────────────────────────

/**
 * Amostra a cor média da borda da imagem (média dos 4 cantos + margens) em
 * canvas — é o fundo da área de destaque da logo. O arquivo é local (input),
 * então o canvas não fica tainted.
 */
async function sampleLogoBackground(file: File): Promise<string | null> {
  try {
    const url = URL.createObjectURL(file)
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image()
        el.onload = () => resolve(el)
        el.onerror = () => reject(new Error('img'))
        el.src = url
      })
      const s = 24
      const canvas = document.createElement('canvas')
      canvas.width = s
      canvas.height = s
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(img, 0, 0, s, s)
      const d = ctx.getImageData(0, 0, s, s).data
      let r = 0
      let g = 0
      let b = 0
      let n = 0
      const at = (x: number, y: number) => {
        const i = (y * s + x) * 4
        r += d[i]!
        g += d[i + 1]!
        b += d[i + 2]!
        n++
      }
      for (let x = 0; x < s; x++) {
        at(x, 0)
        at(x, s - 1)
      }
      for (let y = 0; y < s; y++) {
        at(0, y)
        at(s - 1, y)
      }
      const hex = (v: number) =>
        Math.round(v / n)
          .toString(16)
          .padStart(2, '0')
      return `#${hex(r)}${hex(g)}${hex(b)}`
    } finally {
      URL.revokeObjectURL(url)
    }
  } catch {
    return null
  }
}

function GeneralForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [name, setName] = useState(cfg.name)
  const [about, setAbout] = useState(cfg.about ?? '')
  const fileRef = useRef<HTMLInputElement>(null)

  const mut = useMutation({
    mutationFn: (body: { name: string; about: string | null }) => config.update(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.saved_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const logoMut = useMutation({
    mutationFn: async (file: File) => {
      // amostra a cor de fundo da imagem no browser (arquivo local, sem CORS)
      const bgColor = await sampleLogoBackground(file)
      return config.uploadLogo(file, bgColor)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.logo_updated_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card title={t('admin.branding.general_title')}>
      <div className="space-y-4">
        <Input
          label={t('admin.tenant.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Textarea
          label={t('admin.branding.about')}
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          placeholder={t('admin.branding.about_ph')}
        />

        {/* Logo */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{t('admin.branding.logo')}</p>
          <div className="flex items-center gap-4">
            {cfg.logo_r2_key ? (
              <div className="w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center text-xs text-gray-400 overflow-hidden">
                <span className="text-center px-1">{cfg.logo_r2_key.split('/').pop()}</span>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400">
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                  />
                </svg>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) logoMut.mutate(f)
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                loading={logoMut.isPending}
                onClick={() => fileRef.current?.click()}
              >
                {cfg.logo_r2_key
                  ? t('admin.branding.logo_change')
                  : t('admin.branding.logo_upload')}
              </Button>
              <p className="text-xs text-gray-500">{t('admin.branding.logo_hint')}</p>
            </div>
          </div>
        </div>

        <Button
          loading={mut.isPending}
          onClick={() => mut.mutate({ name: name.trim(), about: about.trim() || null })}
        >
          {t('admin.common.save')}
        </Button>
      </div>
    </Card>
  )
}

// ─── Colors ───────────────────────────────────────────────────────────────────

// Paleta padrão da plataforma (PRATZU · Açafrão) — a mesma que o cardápio usa
// quando o restaurante não customiza nada (MenuApp.tsx); o Reset publica estes valores.
const DEFAULT_BRAND_COLORS = {
  background_color: '#FFF4E0',
  header_color: '#2B0F3A',
  primary_color: '#C4321A',
  button_active_color: '#A82A14',
  secondary_color: '#2B0F3A',
  // null = o cardápio amostra a cor da própria logo (comportamento padrão)
  logo_bg_color: null,
}

function ColorsForm({ cfg, qc }: { cfg: RestaurantConfig; qc: ReturnType<typeof useQueryClient> }) {
  const { t } = useI18n()
  const [background, setBackground] = useState(cfg.background_color)
  const [header, setHeader] = useState(cfg.header_color)
  const [button, setButton] = useState(cfg.primary_color)
  const [buttonActive, setButtonActive] = useState(cfg.button_active_color)
  const [selected, setSelected] = useState(cfg.secondary_color)
  const [logoBg, setLogoBg] = useState(cfg.logo_bg_color ?? '')

  const dirty =
    background !== cfg.background_color ||
    header !== cfg.header_color ||
    button !== cfg.primary_color ||
    buttonActive !== cfg.button_active_color ||
    selected !== cfg.secondary_color ||
    logoBg !== (cfg.logo_bg_color ?? '')

  const mut = useMutation({
    mutationFn: (body: {
      background_color: string
      header_color: string
      primary_color: string
      button_active_color: string
      secondary_color: string
      logo_bg_color: string | null
    }) => config.update(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.colors_published_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const colorFields = [
    {
      key: 'background',
      label: t('admin.branding.color_background'),
      value: background,
      onChange: setBackground,
      help: t('admin.branding.color_background_help'),
    },
    {
      key: 'header',
      label: t('admin.branding.color_header'),
      value: header,
      onChange: setHeader,
      help: t('admin.branding.color_header_help'),
    },
    {
      key: 'button',
      label: t('admin.branding.color_button'),
      value: button,
      onChange: setButton,
      help: t('admin.branding.color_button_help'),
    },
    {
      key: 'buttonActive',
      label: t('admin.branding.color_button_active'),
      value: buttonActive,
      onChange: setButtonActive,
      help: t('admin.branding.color_button_active_help'),
    },
    {
      key: 'selected',
      label: t('admin.branding.color_selected'),
      value: selected,
      onChange: setSelected,
      help: t('admin.branding.color_selected_help'),
    },
    {
      key: 'logoBg',
      label: t('admin.branding.color_logo_bg'),
      value: logoBg,
      onChange: setLogoBg,
      help: t('admin.branding.color_logo_bg_help'),
      optional: true,
    },
  ] satisfies Array<{
    key: string
    label: string
    value: string
    onChange: (v: string) => void
    help: string
    optional?: boolean
  }>

  return (
    <Card title={t('admin.branding.colors_title')} description={t('admin.branding.colors_desc')}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {colorFields.map(({ key, label, value, onChange, help, ...rest }) => (
            <div key={key} className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">{label}</label>
              <p className="text-xs text-gray-500">{help}</p>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={value || '#FFFFFF'}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={value}
                  placeholder={rest.optional ? t('admin.branding.color_auto_ph') : undefined}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v === '' || /^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v)
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  maxLength={7}
                />
                {rest.optional && value !== '' && (
                  <button
                    type="button"
                    onClick={() => onChange('')}
                    className="text-xs text-gray-500 hover:text-gray-700 underline whitespace-nowrap"
                  >
                    {t('admin.branding.color_use_auto')}
                  </button>
                )}
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              loading={mut.isPending}
              disabled={!dirty}
              onClick={() =>
                mut.mutate({
                  background_color: background,
                  header_color: header,
                  primary_color: button,
                  button_active_color: buttonActive,
                  secondary_color: selected,
                  logo_bg_color: logoBg || null,
                })
              }
            >
              {t('admin.branding.colors_publish')}
            </Button>
            <Button
              variant="secondary"
              loading={mut.isPending}
              onClick={() => {
                setBackground(DEFAULT_BRAND_COLORS.background_color)
                setHeader(DEFAULT_BRAND_COLORS.header_color)
                setButton(DEFAULT_BRAND_COLORS.primary_color)
                setButtonActive(DEFAULT_BRAND_COLORS.button_active_color)
                setSelected(DEFAULT_BRAND_COLORS.secondary_color)
                setLogoBg('')
                mut.mutate(DEFAULT_BRAND_COLORS)
              }}
            >
              {t('admin.branding.colors_reset')}
            </Button>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 self-start">
          <p className="text-xs font-medium text-gray-500 mb-2">
            {t('admin.branding.colors_preview_hint')}
          </p>
          <MenuThemePreview
            restaurantName={cfg.name}
            currency={cfg.currency ?? null}
            countryCode={cfg.country_code ?? null}
            colors={{ background, header, button, buttonActive, selected }}
          />
        </div>
      </div>
    </Card>
  )
}

// ─── Operations ───────────────────────────────────────────────────────────────

function OperationsForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  // #332 (0080): o toggle "Restaurante aberto" virou um seletor de 3 opções —
  // automático (interruptor + horário), aberto à força ou fechado à força.
  const [openMode, setOpenMode] = useState<OpenMode>(() => openModeOf(cfg))
  const [allowTakeaway, setAllowTakeaway] = useState(cfg.allow_takeaway)
  const [allowDelivery, setAllowDelivery] = useState(cfg.allow_delivery)
  const [tipMode, setTipMode] = useState(cfg.tip_mode)
  // 0085: fluxo dos pedidos da mesa — completo (cozinha+salão) × via garçom
  const [orderFlow, setOrderFlow] = useState<'full' | 'waiter'>(cfg.order_flow ?? 'full')
  // #129 (0099): pedir NIF/CPF no checkout do cardápio
  const [fiscalMode, setFiscalMode] = useState<'off' | 'optional' | 'required'>(
    cfg.fiscal_id_mode ?? 'off',
  )
  // #133 (0100): SLA de espera do KDS em minutos
  const [slaMinutes, setSlaMinutes] = useState(String(cfg.sla_minutes ?? 15))

  const mut = useMutation({
    mutationFn: (body: Partial<RestaurantConfig>) => config.update(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.saved_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const openModeOptions: { value: OpenMode; label: string; description: string }[] = [
    {
      value: 'auto',
      label: t('admin.branding.ops_open_mode_auto'),
      description: t('admin.branding.ops_open_mode_auto_desc'),
    },
    {
      value: 'open',
      label: t('admin.branding.ops_open_mode_open'),
      description: t('admin.branding.ops_open_mode_open_desc'),
    },
    {
      value: 'closed',
      label: t('admin.branding.ops_open_mode_closed'),
      description: t('admin.branding.ops_open_mode_closed_desc'),
    },
  ]

  function saveOperations() {
    const sla = Number(slaMinutes)
    if (!Number.isInteger(sla) || sla < 1 || sla > 240) {
      toast.error(t('admin.branding.ops_sla_invalid'))
      return
    }
    mut.mutate({
      ...openModePayload(openMode),
      allow_takeaway: allowTakeaway,
      allow_delivery: allowDelivery,
      tip_mode: tipMode,
      order_flow: orderFlow,
      fiscal_id_mode: fiscalMode,
      sla_minutes: sla,
    })
  }

  return (
    <Card title={t('admin.nav.section.operations')}>
      <div className="space-y-4">
        {/* radio group nativo (fieldset/legend) — teclado e leitor de tela sem JS */}
        <fieldset data-testid="open-mode" className="space-y-2">
          <legend className="text-sm font-medium text-gray-700 mb-1">
            {t('admin.branding.ops_open_mode')}
          </legend>
          {openModeOptions.map((opt) => (
            <label
              key={opt.value}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-2.5 cursor-pointer',
                openMode === opt.value
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:bg-gray-50',
              )}
            >
              <input
                type="radio"
                name="open_mode"
                value={opt.value}
                checked={openMode === opt.value}
                onChange={() => setOpenMode(opt.value)}
                aria-describedby={`open-mode-${opt.value}-desc`}
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600 focus:ring-2 focus:ring-indigo-500"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                <span id={`open-mode-${opt.value}-desc`} className="block text-xs text-gray-500">
                  {opt.description}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        <Switch
          checked={allowTakeaway}
          onChange={setAllowTakeaway}
          label={t('admin.branding.ops_takeaway')}
          description={t('admin.branding.ops_takeaway_desc')}
        />
        <Switch
          checked={allowDelivery}
          onChange={setAllowDelivery}
          label={t('admin.branding.ops_delivery')}
          description={t('admin.branding.ops_delivery_desc')}
        />
        {/* 0085: como o pedido da mesa chega à casa — pipeline PRATZU ou via
            garçom (o cliente envia, o garçom lança no sistema local/POS) */}
        <Select
          label={t('admin.branding.ops_order_flow')}
          value={orderFlow}
          onChange={(e) => setOrderFlow(e.target.value as 'full' | 'waiter')}
          options={[
            { value: 'full', label: t('admin.branding.ops_order_flow_full') },
            { value: 'waiter', label: t('admin.branding.ops_order_flow_waiter') },
          ]}
        />
        <p className="-mt-2 text-xs text-gray-500">
          {t(
            orderFlow === 'waiter'
              ? 'admin.branding.ops_order_flow_waiter_desc'
              : 'admin.branding.ops_order_flow_full_desc',
          )}
        </p>
        <Select
          label={t('admin.branding.ops_tip_mode')}
          value={tipMode}
          onChange={(e) => setTipMode(e.target.value as typeof tipMode)}
          options={[
            { value: 'none', label: t('admin.branding.ops_tip_none') },
            { value: 'optional', label: t('admin.branding.ops_tip_optional') },
            { value: 'suggested', label: t('admin.branding.ops_tip_suggested') },
          ]}
        />
        {/* #129: identificação fiscal no recibo — escolha do dono */}
        <Select
          label={t('admin.branding.ops_fiscal_mode')}
          value={fiscalMode}
          data-testid="fiscal-mode"
          onChange={(e) => setFiscalMode(e.target.value as typeof fiscalMode)}
          options={[
            { value: 'off', label: t('admin.branding.ops_fiscal_off') },
            { value: 'optional', label: t('admin.branding.ops_fiscal_optional') },
            { value: 'required', label: t('admin.branding.ops_fiscal_required') },
          ]}
        />
        <p className="-mt-2 text-xs text-gray-500">{t('admin.branding.ops_fiscal_mode_desc')}</p>
        {/* #133: limiar do destaque vermelho no monitor da cozinha */}
        <Input
          label={t('admin.branding.ops_sla')}
          inputMode="numeric"
          value={slaMinutes}
          data-testid="sla-minutes"
          onChange={(e) => setSlaMinutes(e.target.value)}
        />
        <p className="-mt-2 text-xs text-gray-500">{t('admin.branding.ops_sla_desc')}</p>
        <Button loading={mut.isPending} onClick={saveOperations} data-testid="ops-save">
          {t('admin.branding.ops_save')}
        </Button>
      </div>
    </Card>
  )
}

// ─── Limite do garçom (ajustes de preço, #274/#284) ──────────────────────────

function WaiterLimitForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [mode, setMode] = useState<'none' | 'percent' | 'amount'>(
    cfg.waiter_adjust_limit_type ?? 'none',
  )
  // percent: inteiro; amount: em unidades da moeda (ex.: 10,50) → centavos
  const initial =
    cfg.waiter_adjust_limit_value === null || cfg.waiter_adjust_limit_value === undefined
      ? ''
      : cfg.waiter_adjust_limit_type === 'amount'
        ? (cfg.waiter_adjust_limit_value / 100).toFixed(2)
        : String(cfg.waiter_adjust_limit_value)
  const [value, setValue] = useState(initial)

  const mut = useMutation({
    mutationFn: (body: Partial<RestaurantConfig>) => config.update(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.saved_toast'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function save() {
    if (mode === 'none') {
      mut.mutate({ waiter_adjust_limit_type: null, waiter_adjust_limit_value: null })
      return
    }
    const n = Number(value.replace(',', '.'))
    if (!Number.isFinite(n) || n < 0 || (mode === 'percent' && n > 100)) {
      toast.error(t('admin.waiter_limit.invalid'))
      return
    }
    mut.mutate({
      waiter_adjust_limit_type: mode,
      waiter_adjust_limit_value: mode === 'amount' ? Math.round(n * 100) : Math.round(n),
    })
  }

  return (
    <Card title={t('admin.waiter_limit.title')}>
      <div className="space-y-4" data-testid="waiter-limit-form">
        <p className="text-sm text-gray-600">{t('admin.waiter_limit.desc')}</p>
        <Select
          label={t('admin.waiter_limit.mode')}
          value={mode}
          data-testid="waiter-limit-mode"
          onChange={(e) => setMode(e.target.value as typeof mode)}
          options={[
            { value: 'none', label: t('admin.waiter_limit.mode_none') },
            { value: 'percent', label: t('admin.waiter_limit.mode_percent') },
            { value: 'amount', label: t('admin.waiter_limit.mode_amount') },
          ]}
        />
        {mode !== 'none' && (
          <Input
            label={
              mode === 'percent'
                ? t('admin.waiter_limit.value_percent')
                : t('admin.waiter_limit.value_amount')
            }
            inputMode="decimal"
            value={value}
            data-testid="waiter-limit-value"
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        <Button loading={mut.isPending} onClick={save} data-testid="waiter-limit-save">
          {t('admin.waiter_limit.save')}
        </Button>
      </div>
    </Card>
  )
}

// ─── Payments ────────────────────────────────────────────────────────────────

function PaymentsForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const normalize = (methods: string[] | undefined) =>
    methods && methods.length > 0 ? methods : ['cash', 'card_local']
  const [cash, setCash] = useState(normalize(cfg.payment_methods).includes('cash'))
  const [card, setCard] = useState(normalize(cfg.payment_methods).includes('card_local'))

  const mut = useMutation({
    mutationFn: (body: Partial<RestaurantConfig>) => config.update(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.pay_saved'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const save = () => {
    const methods = [...(cash ? ['cash'] : []), ...(card ? ['card_local'] : [])]
    if (methods.length === 0) {
      toast.error(t('admin.branding.pay_hint'))
      return
    }
    mut.mutate({ payment_methods: methods })
  }

  return (
    <Card title={t('admin.branding.pay_title')}>
      <div className="space-y-4">
        <p className="text-sm text-gray-500">{t('admin.branding.pay_hint')}</p>
        <Switch checked={cash} onChange={setCash} label={t('admin.branding.pay_cash')} />
        <Switch checked={card} onChange={setCard} label={t('admin.branding.pay_card')} />
        <Button loading={mut.isPending} onClick={save}>
          {t('admin.branding.ops_save')}
        </Button>
      </div>
    </Card>
  )
}

// ─── Hours ────────────────────────────────────────────────────────────────────

function HoursForm({
  hours,
  qc,
}: {
  hours: OperatingHour[]
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t, lang } = useI18n()
  type DayHour = { open_time: string | null; close_time: string | null }
  const [local, setLocal] = useState<DayHour[]>(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const h = hours.find((x) => x.day_of_week === i)
      return { open_time: h?.open_time ?? null, close_time: h?.close_time ?? null }
    })
  })

  useEffect(() => {
    setLocal(
      Array.from({ length: 7 }, (_, i) => {
        const h = hours.find((x) => x.day_of_week === i)
        return { open_time: h?.open_time ?? null, close_time: h?.close_time ?? null }
      }),
    )
  }, [hours])

  const mut = useMutation({
    // #332: só dias com janela completa — guardar tudo vazio limpa o horário
    // (GET devolve []; o menu trata como "sem restrição de horário") em vez de
    // criar 7 linhas "fechado"
    mutationFn: () => config.setHours(hoursPayload(local)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hours'] })
      toast.success(t('admin.branding.hours_saved_toast'))
    },
    // INCOMPLETE_HOURS traduzido pelo código, com os dias (notas do QA)
    onError: (e: Error) => toast.error(hoursErrorMessage(e, t, lang)),
  })

  function toggle(i: number) {
    setLocal((prev) =>
      prev.map((h, idx) =>
        idx === i
          ? h.open_time
            ? { open_time: null, close_time: null }
            : { open_time: '12:00', close_time: '22:00' }
          : h,
      ),
    )
  }

  function setTime(i: number, field: 'open_time' | 'close_time', value: string) {
    setLocal((prev) => prev.map((h, idx) => (idx === i ? { ...h, [field]: value || null } : h)))
  }

  return (
    <Card title={t('admin.branding.hours_title')}>
      <div className="space-y-3">
        {Array.from({ length: 7 }, (_, i) => {
          const day = t(`admin.branding.hours.day_${i}`)
          const h = local[i]!
          const open = h.open_time !== null
          return (
            <div key={day} className="flex items-center gap-4">
              <div className="w-20 flex-shrink-0">
                <span
                  className={cn('text-sm', open ? 'text-gray-900 font-medium' : 'text-gray-400')}
                >
                  {day}
                </span>
              </div>
              <Switch checked={open} onChange={() => toggle(i)} />
              {open ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="time"
                    value={h.open_time ?? ''}
                    onChange={(e) => setTime(i, 'open_time', e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="text-gray-400 text-sm">{t('admin.branding.hours.until')}</span>
                  <input
                    type="time"
                    value={h.close_time ?? ''}
                    onChange={(e) => setTime(i, 'close_time', e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              ) : (
                <span className="text-sm text-gray-400">{t('admin.branding.hours.closed')}</span>
              )}
            </div>
          )
        })}
        <Button loading={mut.isPending} onClick={() => mut.mutate()} className="mt-4">
          {t('admin.branding.hours_save')}
        </Button>
      </div>
    </Card>
  )
}
